const MAX_VISIBLE_OBJECTS = 40;
const MAX_AVAILABLE_ACTIONS = 24;
const MAX_VISITED_PORTALS = 12;
const MAX_PORTAL_HISTORY = 48;
const MAX_INTERACTION_TIMELINE = 2400;
const MAX_CONTEXT_INTERACTION_TIMELINE = 240;
const MAX_TEXT_LENGTH = 320;
const PORTAL_HISTORY_STORAGE_KEY = "joz-live-portal-history-v1";
import { worldxLandmarkFromView } from "./portalSpatialModel";
const WORLDX_OBJECT_IDS = new Set([
  "meet_joz_semantic_city",
  "meet_joz_heart",
  "meet_joz_scale_maxx",
  "meet_joz_clout_maxx",
  "meet_joz_world_class",
  "meet_joz_alpha_psl",
  "meet_joz_atmos_maxx",
  "meet_joz_cross_sensory_aura_engineering",
  "meet_joz_maximize_beauty_change_reality",
  "meet_joz_ai_synthesis",
  "meet_joz_ai_analysis",
  "meet_joz_capsule",
]);

function clean(value, fallback = null) {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized || fallback;
}

function unique(values, limit) {
  return [...new Set(values.map((value) => clean(value)).filter(Boolean))].slice(0, limit);
}

function boundedText(value, fallback = null) {
  const text = String(value || "").trim();
  return text ? text.slice(0, MAX_TEXT_LENGTH) : fallback;
}

function finiteNumber(value, fallback = null) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function awarenessContext(perceptionState = {}, currentPortal = "root") {
  const awareness = perceptionState.awareness;
  if (!awareness || awareness.portal !== currentPortal) return null;
  return {
    source: "verified_live_perception",
    observed_at: boundedText(awareness.observedAt),
    situation: boundedText(awareness.situation),
    looking_at: boundedText(awareness.lookingAt),
    focus_object_id: clean(awareness.focusObjectId),
    salient_object_ids: unique(awareness.salientObjectIds || [], 12),
    changes: (awareness.changes || []).map((value) => boundedText(value)).filter(Boolean).slice(0, 8),
    uncertainties: (awareness.uncertainties || []).map((value) => boundedText(value)).filter(Boolean).slice(0, 8),
    confidence: clean(awareness.confidence, "unknown"),
    evidence_ids: unique(awareness.evidenceIds || [], 16),
  };
}

export function normalizeLivePortal(value, path = "") {
  const pathMatch = String(path || "").match(/^\/neo\/([^/]+)/i);
  const portal = clean(pathMatch?.[1] || value, "root");
  if (portal === "meet-joz" || portal === "meet_joz") return "meet_joz";
  if (portal === "the-vibe-energy" || portal === "maxx") return "maxx";
  return portal;
}

export function normalizePortalHistory(value, limit = MAX_PORTAL_HISTORY) {
  const history = Array.isArray(value)
    ? value
      .map((entry) => typeof entry === "string" ? entry : entry?.portal || entry?.portalId)
      .map((entry) => normalizeLivePortal(entry))
      .filter(Boolean)
    : [];
  return history.slice(-Math.max(1, Number(limit) || MAX_PORTAL_HISTORY));
}

function readStoredPortalHistory() {
  if (typeof window === "undefined") return [];
  try {
    return normalizePortalHistory(JSON.parse(window.sessionStorage.getItem(PORTAL_HISTORY_STORAGE_KEY) || "[]"));
  } catch {
    return [];
  }
}

function writeStoredPortalHistory(history) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(PORTAL_HISTORY_STORAGE_KEY, JSON.stringify(history));
  } catch {
    // Storage can be unavailable in private browsing or test environments.
  }
}

function normalizeInteractionTimeline(value, limit = MAX_INTERACTION_TIMELINE) {
  return (Array.isArray(value) ? value : [])
    .filter((event) => event && typeof event === "object")
    .slice(-Math.max(1, Number(limit) || MAX_INTERACTION_TIMELINE));
}

function appendInteractionTimelineEvent(event) {
  if (typeof window === "undefined" || !event) return null;
  const current = window.__liveAppPerceptionState || {};
  const sequence = Number(current.interactionTimelineSequence || 0) + 1;
  const nextEvent = { sequence, ...event };
  const interactionTimeline = normalizeInteractionTimeline([
    ...(current.interactionTimeline || []),
    nextEvent,
  ]);
  window.__liveAppPerceptionState = {
    ...current,
    interactionTimeline,
    interactionTimelineSequence: sequence,
  };

  if (window.__liveAppContext) {
    window.__liveAppContext = {
      ...window.__liveAppContext,
      interaction_timeline: interactionTimeline.slice(-MAX_CONTEXT_INTERACTION_TIMELINE),
      interaction_timeline_count: sequence,
    };
  }

  const now = Date.now();
  const lastPublishedAt = Number(window.__interactionTimelinePublishedAt || 0);
  if (event.type !== "camera" || now - lastPublishedAt >= 250) {
    window.__interactionTimelinePublishedAt = now;
    window.dispatchEvent(new CustomEvent("world-interaction-timeline-updated", {
      detail: {
        sequence,
        type: event.type,
        portal: event.portal || null,
      },
    }));
  }
  return nextEvent;
}

function semanticObjectIds(sceneState, currentPortal, isMobile = false) {
  return unique(
    (sceneState?.visibleSemanticObjects || [])
      .filter((object) => {
        const objectPortal = normalizeLivePortal(object?.portalId);
        return !object?.portalId || objectPortal === currentPortal;
      })
      .map((object) => object?.id)
      .filter((id) => !(isMobile && WORLDX_OBJECT_IDS.has(clean(id)))),
    MAX_VISIBLE_OBJECTS
  );
}

function semanticActionIds(sceneState) {
  return (sceneState?.availableSemanticActions || []).map((action) =>
    typeof action === "string" ? action : action?.id || action?.action
  );
}

function semanticObjectDetails(sceneState, currentPortal, isMobile) {
  return (sceneState?.visibleSemanticObjects || [])
    .filter((object) => {
      const objectPortal = normalizeLivePortal(object?.portalId);
      return !object?.portalId || objectPortal === currentPortal;
    })
    .map((object) => ({
      id: clean(object?.id),
      role: boundedText(object?.role),
      meaning: boundedText(object?.meaning),
      source_parent_id: clean(object?.sourceParentId),
      spatial: object?.spatial
        ? {
            relation: clean(object.spatial.relation),
            bearing_degrees: finiteNumber(object.spatial.bearing_degrees),
            elevation_degrees: finiteNumber(object.spatial.elevation_degrees),
            distance: finiteNumber(object.spatial.distance),
            source: clean(object.spatial.source),
          }
        : null,
    }))
    .filter((object) => object.id && !(isMobile && WORLDX_OBJECT_IDS.has(object.id)))
    .slice(0, MAX_VISIBLE_OBJECTS);
}

function cameraViewContext(sceneSnapshot, matchingScene, currentPortal, isMobile) {
  if (!matchingScene?.activePortal && !matchingScene?.sceneId) return null;
  const view = sceneSnapshot?.cameraState?.view;
  if (!view) return null;
  const lastRotation = view.last_rotation;
  const centerEvidence = sceneSnapshot?.worldxCenterEvidence;
  const centerSector = Number(centerEvidence?.sectorIndex);
  const derivedSector = worldxLandmarkFromView({
    headingDegrees: Number(view.yaw_degrees),
    pitchDegrees: Number(view.pitch_degrees),
  })?.sectorIndex;
  const relativeWorldxSector = currentPortal === "meet_joz" && !isMobile
    ? Number.isInteger(centerSector) && centerSector >= 0 && centerSector < 16
      ? centerSector
      : Number.isInteger(derivedSector) ? derivedSector : null
    : null;
  return {
    horizontal_sector: clean(view.horizontal_sector),
    vertical_band: clean(view.vertical_band),
    yaw_degrees: finiteNumber(view.yaw_degrees),
    heading_degrees: finiteNumber(
      Number.isFinite(Number(view.yaw_degrees))
        ? ((Number(view.yaw_degrees) % 360) + 360) % 360
        : null
    ),
    pitch_degrees: finiteNumber(view.pitch_degrees),
    previous_horizontal_sector: clean(view.previous_horizontal_sector),
    rotation_direction: clean(view.rotation_direction),
    rotation_delta_degrees: finiteNumber(view.rotation_delta_degrees),
    yaw_velocity: finiteNumber(view.yaw_velocity),
    pitch_velocity: finiteNumber(view.pitch_velocity),
    stability: finiteNumber(view.stability),
    relative_worldx_sector: relativeWorldxSector,
    last_rotation: lastRotation
      ? {
          from_sector: clean(lastRotation.from_sector),
          toward_sector: clean(lastRotation.toward_sector),
          direction: clean(lastRotation.direction),
          degrees: finiteNumber(lastRotation.degrees),
          observed_at: boundedText(lastRotation.observed_at),
        }
      : null,
  };
}

function lookAtEvidenceContext(sceneSnapshot, matchingScene) {
  if (!matchingScene?.activePortal && !matchingScene?.sceneId) return null;
  const evidence = sceneSnapshot?.lookAtEvidence;
  if (!evidence || !["center-ray", "portal-spatial-model"].includes(evidence.method)) return null;
  return {
    method: evidence.method,
    primary_object_id: clean(evidence.primaryObjectId),
    candidate_object_ids: unique(
      (evidence.candidates || []).map((candidate) => candidate?.id),
      8
    ),
    candidates: (evidence.candidates || []).slice(0, 8).map((candidate) => ({
      id: clean(candidate?.id),
      hit_distance: finiteNumber(candidate?.hitDistance),
      hit_object_id: clean(candidate?.hitObjectId),
      asset: boundedText(candidate?.asset),
      bearing_degrees: finiteNumber(candidate?.bearingDegrees),
      elevation_degrees: finiteNumber(candidate?.elevationDegrees),
      angular_distance_degrees: finiteNumber(candidate?.angularDistanceDegrees),
    })).filter((candidate) => candidate.id),
    confidence: clean(evidence.confidence, "medium"),
    model_version: boundedText(evidence.modelVersion),
    sector_id: clean(evidence.sectorId),
    elevation_band: clean(evidence.elevationBand),
  };
}

function perceptionMetricsContext({ cameraView, lookAtEvidence, sceneSnapshot } = {}) {
  const primaryCandidate = lookAtEvidence?.candidates?.find(
    (candidate) => candidate?.id === lookAtEvidence?.primary_object_id
  ) || lookAtEvidence?.candidates?.[0] || null;
  const performance = typeof window !== "undefined" ? window.__scenePerformanceStats || {} : {};
  const viewStats = performance.cameraViewStats || {};
  const rendererStats = performance.rendererStats || {};

  return {
    elevation_degrees: finiteNumber(cameraView?.pitch_degrees),
    pitch_degrees: finiteNumber(cameraView?.pitch_degrees),
    azimuth_degrees: finiteNumber(cameraView?.yaw_degrees),
    yaw_degrees: finiteNumber(cameraView?.yaw_degrees),
    heading_degrees: finiteNumber(cameraView?.heading_degrees),
    horizontal_sector: clean(cameraView?.horizontal_sector),
    vertical_band: clean(cameraView?.vertical_band),
    track_object_id: clean(lookAtEvidence?.primary_object_id),
    target_bearing_degrees: finiteNumber(primaryCandidate?.bearing_degrees),
    target_elevation_degrees: finiteNumber(primaryCandidate?.elevation_degrees),
    target_distance: finiteNumber(primaryCandidate?.hit_distance),
    target_angular_distance_degrees: finiteNumber(primaryCandidate?.angular_distance_degrees),
    vertices: finiteNumber(viewStats.vertices),
    faces: finiteNumber(viewStats.faces),
    fps: finiteNumber(rendererStats.fps),
    frame_time_ms: finiteNumber(rendererStats.ms),
    observed_at: boundedText(sceneSnapshot?.observedAt),
  };
}

export function buildLiveAppContext({
  appState = {},
  sceneSnapshot = null,
  perceptionState = {},
} = {}) {
  const currentPortal = normalizeLivePortal(appState.currentPortal, appState.currentPath);
  const sceneState = sceneSnapshot?.sceneState || {};
  const scenePortal = normalizeLivePortal(sceneState.activePortal || sceneState.sceneId);
  const matchingScene = scenePortal === currentPortal ? sceneState : {};
  const isMobile = Boolean(appState.uiState?.isMobile);
  const focusedObject = clean(
    matchingScene.focusedEntityId || appState.currentMesh || perceptionState.focusedObject
  );
  const visibleObjectDetails = semanticObjectDetails(matchingScene, currentPortal, isMobile);
  const visibleObjects = visibleObjectDetails.length
    ? visibleObjectDetails.map((object) => object.id)
    : semanticObjectIds(matchingScene, currentPortal, isMobile);
  const cameraView = cameraViewContext(sceneSnapshot, matchingScene, currentPortal, isMobile);
  const lookAtEvidence = lookAtEvidenceContext(sceneSnapshot, matchingScene);
  const availableActions = unique(
    [
      ...(appState.allowedActions || []),
      ...semanticActionIds(matchingScene),
    ].map((action) => typeof action === "string" ? action : action?.id || action?.type),
    MAX_AVAILABLE_ACTIONS
  );
  const knownHistory = normalizePortalHistory(
    perceptionState.portalHistory
      || perceptionState.portal_history
      || perceptionState.visitedPortals
  );
  const portalHistory = knownHistory.length
    ? knownHistory[knownHistory.length - 1] === currentPortal
      ? knownHistory
      : [...knownHistory, currentPortal].slice(-MAX_PORTAL_HISTORY)
    : [currentPortal];
  const visitedPortals = unique(
    portalHistory,
    MAX_VISITED_PORTALS
  );
  const interactionTimeline = normalizeInteractionTimeline(
    perceptionState.interactionTimeline || perceptionState.interaction_timeline
  );
  const currentStage = clean(matchingScene.activeStage || appState.currentMeshStage);
  const transitionState = clean(
    perceptionState.transitionState || appState.voiceState?.currentPhase || currentStage
  );

  return {
    current_portal: currentPortal,
    current_stage: currentStage,
    focused_object: focusedObject,
    visible_objects: visibleObjects,
    visible_object_details: visibleObjectDetails,
    spatial_objects: visibleObjectDetails
      .filter((object) => object.spatial)
      .map((object) => ({ id: object.id, ...object.spatial })),
    camera_view: cameraView,
    look_at_evidence: lookAtEvidence,
    perception_metrics: perceptionMetricsContext({ cameraView, lookAtEvidence, sceneSnapshot }),
    experience_capabilities: {
      worldx: currentPortal === "meet_joz" && !isMobile,
    },
    available_actions: availableActions,
    visited_portals: visitedPortals,
    portal_history: portalHistory,
    interaction_timeline: interactionTimeline.slice(-MAX_CONTEXT_INTERACTION_TIMELINE),
    interaction_timeline_count: Number(perceptionState.interactionTimelineSequence || interactionTimeline.length),
    previous_portal: clean(perceptionState.previousPortal || portalHistory[portalHistory.length - 2]),
    previous_action: clean(perceptionState.previousAction),
    previous_target: clean(perceptionState.previousTarget),
    previous_interaction_at: perceptionState.previousInteractionAt || null,
    transition_state: transitionState,
    device: {
      class: isMobile ? "mobile" : "desktop",
      mobile: isMobile,
      ar_supported: Boolean(appState.uiState?.arSupported),
    },
    observation: {
      source: sceneSnapshot ? "rendered_scene" : "runtime_state",
      observed_at: sceneSnapshot?.observedAt || new Date().toISOString(),
      semantic_object_count: visibleObjects.length,
    },
    awareness: awarenessContext(perceptionState, currentPortal),
  };
}

export function publishLiveAppContext({ appState, sceneSnapshot } = {}) {
  if (typeof window === "undefined") return null;

  const nextState = appState || window.__appState || {};
  const perceptionState = window.__liveAppPerceptionState || {};
  const currentPortal = normalizeLivePortal(nextState.currentPortal, nextState.currentPath);
  const publishedPortal = clean(window.__liveAppContext?.current_portal);
  const portalChanged = Boolean(publishedPortal && publishedPortal !== currentPortal);
  const knownHistory = normalizePortalHistory(
    perceptionState.portalHistory || window.__liveAppContext?.portal_history || readStoredPortalHistory()
  );
  const portalHistory = knownHistory.length
    ? knownHistory[knownHistory.length - 1] === currentPortal
      ? knownHistory
      : [...knownHistory, currentPortal].slice(-MAX_PORTAL_HISTORY)
    : [currentPortal];
  const nextPerceptionState = portalChanged
    ? { ...perceptionState, previousPortal: publishedPortal, portalHistory }
    : { ...perceptionState, portalHistory };
  const appContext = buildLiveAppContext({
    appState: nextState,
    sceneSnapshot: sceneSnapshot || window.__sceneObservationSnapshot,
    perceptionState: nextPerceptionState,
  });

  window.__liveAppPerceptionState = {
    ...nextPerceptionState,
    visitedPortals: appContext.visited_portals,
    portalHistory: appContext.portal_history,
  };
  window.__liveAppContext = appContext;
  writeStoredPortalHistory(appContext.portal_history);
  window.dispatchEvent(new CustomEvent("live-app-context-updated", { detail: appContext }));
  return appContext;
}

export function recordLiveAppInteraction(action, target = null, { portal = null } = {}) {
  if (typeof window === "undefined") return null;

  const current = window.__liveAppPerceptionState || {};
  const interactionPortal = clean(
    portal || window.__liveAppContext?.current_portal || window.__appState?.currentPortal,
    "root"
  );
  appendInteractionTimelineEvent({
    type: "action",
    portal: interactionPortal,
    action: clean(action),
    target: clean(target),
    observed_at: new Date().toISOString(),
  });
  const next = window.__liveAppPerceptionState || current;
  window.__liveAppPerceptionState = {
    ...next,
    previousAction: clean(action),
    previousTarget: clean(target),
    previousInteractionAt: new Date().toISOString(),
  };
  return publishLiveAppContext();
}

export function recordLiveCameraTelemetry({
  portal,
  cameraView = {},
  lookAtEvidence = null,
  active = true,
} = {}) {
  if (typeof window === "undefined" || !cameraView) return null;
  return appendInteractionTimelineEvent({
    type: "camera",
    portal: normalizeLivePortal(portal || window.__liveAppContext?.current_portal),
    active: active === true,
    observed_at: new Date().toISOString(),
    yaw_degrees: finiteNumber(cameraView.yaw_degrees),
    pitch_degrees: finiteNumber(cameraView.pitch_degrees),
    horizontal_sector: clean(cameraView.horizontal_sector),
    vertical_band: clean(cameraView.vertical_band),
    rotation_direction: clean(cameraView.rotation_direction),
    rotation_delta_degrees: finiteNumber(cameraView.rotation_delta_degrees),
    yaw_velocity: finiteNumber(cameraView.yaw_velocity),
    pitch_velocity: finiteNumber(cameraView.pitch_velocity),
    looking_at: clean(lookAtEvidence?.primaryObjectId),
    look_at_method: clean(lookAtEvidence?.method),
  });
}

export function recordLivePortalAwareness({ portal, perception, evidenceIds = [] } = {}) {
  if (typeof window === "undefined" || !perception) return null;
  const current = window.__liveAppPerceptionState || {};
  window.__liveAppPerceptionState = {
    ...current,
    awareness: {
      portal: normalizeLivePortal(portal),
      observedAt: new Date().toISOString(),
      situation: boundedText(perception.situation || perception.lookingAt),
      lookingAt: boundedText(perception.lookingAt),
      focusObjectId: clean(perception.focusObjectId),
      salientObjectIds: unique(perception.salientObjectIds || [], 12),
      changes: (perception.changes || []).map((value) => boundedText(value)).filter(Boolean).slice(0, 8),
      uncertainties: (perception.uncertainties || []).map((value) => boundedText(value)).filter(Boolean).slice(0, 8),
      confidence: clean(perception.confidence, "unknown"),
      evidenceIds: unique(evidenceIds, 16),
    },
  };
  return publishLiveAppContext();
}

export function getLiveAppContext(appState = null) {
  if (typeof window === "undefined") {
    return buildLiveAppContext({ appState: appState || {} });
  }
  return publishLiveAppContext({ appState: appState || window.__appState || {} });
}
